import { isMilestoneEvent } from "@reactive-resume/dto";
import { useEffect } from "react";
import { useLocation } from "react-router";

import { restoreConsentIfAccepted } from "../../providers/cookie-consent";
import {
  initializeAnalytics,
  pageView,
  trackJourneyStep,
  trackMilestone,
} from "../../services/analytics/google-analytics";

/**
 * GoogleAnalytics component
 *
 * Mounts in root provider and:
 * 1. Initializes GA on first mount
 * 2. Tracks page views on React Router location changes (pathname only, strips sensitive query params)
 * 3. Intercepts clicks on elements with `data-track` attribute and routes them to appropriate trackers
 *
 * Consent Mode v2 default (denied) is set during initializeAnalytics.
 */
export const GoogleAnalytics = () => {
  const location = useLocation();

  // Initialize GA on mount
  useEffect(() => {
    initializeAnalytics();
    // Restore previously-accepted consent for returning users
    restoreConsentIfAccepted();

    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const trackableElement = target.closest("[data-track]");

      if (trackableElement) {
        const eventName = (trackableElement as HTMLElement).dataset.track;
        const eventParamsString = (trackableElement as HTMLElement).dataset.trackParams;
        let eventParams = {};

        if (eventParamsString) {
          try {
            eventParams = JSON.parse(eventParamsString);
          } catch {
            // eslint-disable-next-line no-console
            console.warn(`[GA] Invalid JSON in data-track-params:`, eventParamsString);
          }
        }

        if (eventName) {
          // Route to correct tracker based on event category
          if (isMilestoneEvent(eventName)) {
            trackMilestone(eventName, eventParams);
          } else {
            trackJourneyStep(eventName, eventParams);
          }
        }
      }
    };

    document.addEventListener("click", handleGlobalClick);

    return () => {
      document.removeEventListener("click", handleGlobalClick);
    };
  }, []);

  // Track page views on location change
  // Only track pathname, never include query params (may contain tokens)
  useEffect(() => {
    pageView(location.pathname);
  }, [location]);

  return null;
};
